/**
 * SearchVariant — AI search result card.
 * Uses shared EngagementBar + ShareSection.
 * Unique: Category badge, top pick highlight, meta row (distance/prep time/difficulty).
 */
import { useState, useMemo, useCallback, memo } from 'react';
import { Star, MapPin, Clock, ChefHat, Navigation, UtensilsCrossed, ShoppingBag, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFoodlist } from '../../contexts/FoodlistContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import AuthModal from '../AuthModal';
import EngagementBar from './EngagementBar';
import ShareSection from './ShareSection';
import { useFoodImage } from '../../hooks/useFoodImage';
import { useEngagement } from '../../hooks/useEngagement';
import { buildShareHandler, DirectionsPlaceholder, API_URL } from './shared';

const CATEGORY_META = {
  dine_out: { label: 'Dine Out', icon: UtensilsCrossed, color: '#FF6B35' },
  order_in: { label: 'Order In', icon: ShoppingBag, color: '#3B82F6' },
  cook_at_home: { label: 'Cook It', icon: ChefHat, color: '#10B981' },
};

const SearchVariant = ({ result, isTopPick }) => {
  const cat = CATEGORY_META[result.category] || CATEGORY_META.dine_out;
  const CatIcon = cat.icon;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isInFoodlist, toggleFoodlist } = useFoodlist();
  const { toast } = useToast();
  const { src: foodImgSrc, source: foodImgSource, attribution: foodImgAttribution, handleError: handleFoodImgError } = useFoodImage(result.name, result.image, result.venue_name || '');

  const { repostCount, reposted, shareCount, toggleRepost, recordShare } = useEngagement(
    result.name || '', result.venue_name || '', '', user?.user_id || ''
  );
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [saveAnim, setSaveAnim] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [vibeDisliked, setVibeDisliked] = useState(false);

  const foodlistItem = useMemo(() => ({ name: result.name || '', restaurant: result.venue_name || '', location: result.suburb || '', image: result.image || '', cuisine: result.cuisine || '' }), [result]);
  const inFoodlist = isInFoodlist(foodlistItem.name, foodlistItem.restaurant);
  const [foodlistCount, setFoodlistCount] = useState(result.foodlist_count || 0);

  const testId = useMemo(() => (result.id || result.name || 'item').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30), [result.id, result.name]);

  const handleRepost = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    toggleRepost();
  }, [user, toggleRepost]);

  const handleFoodlistToggle = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    toggleFoodlist(foodlistItem);
    if (!inFoodlist) { setSaveAnim(true); setFoodlistCount(c => c + 1); setTimeout(() => setSaveAnim(false), 800); }
    else { setFoodlistCount(c => Math.max(0, c - 1)); }
  }, [user, foodlistItem, toggleFoodlist, inFoodlist]);

  const handleFoodCardTap = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    navigate('/create-foodcard', { state: { prefill: { name: result.name, restaurant: result.venue_name, cuisine: result.cuisine, image: result.image }, claimMode: true } });
  }, [user, result, navigate]);

  const handleNotMyVibe = useCallback(async () => {
    if (!user) { setShowAuthModal(true); return; }
    setVibeDisliked(true);
    try {
      await fetch(`${API_URL}/api/preferences/not-my-vibe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ item_name: result.name || '', cuisine: result.cuisine || '', category: result.category || 'dine_out', reason: 'not_interested', source: 'search' }),
      });
    } catch {}
  }, [user, result]);

  const shareUrl = `${window.location.origin}/ai-chat?q=${encodeURIComponent(result.name || '')}`;
  const baseShareHandler = useMemo(() => buildShareHandler(result.name, result.venue_name, shareUrl), [result.name, result.venue_name, shareUrl]);
  const handleSocialShare = useCallback(async (platform) => { await baseShareHandler(platform); recordShare(platform); if (platform === 'copy') toast({ title: 'Link copied!' }); }, [baseShareHandler, recordShare, toast]);

  return (
    <>
      <article data-testid={`search-result-${result.id}`}
        className={`group bg-white rounded-[22px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] transition-all duration-300 ${isTopPick ? 'ring-2 ring-[#F4C430]' : ''}`}>
        {foodImgSrc ? (
        <div className="relative h-44 overflow-hidden group">
          <img src={foodImgSrc} alt={result.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            onError={handleFoodImgError} loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          {foodImgAttribution && <div className="absolute bottom-10 right-2.5 bg-black/[0.32] backdrop-blur-md border border-white/[0.12] px-2 py-0.5 rounded-full"><span className="text-white/90 text-[10px] font-medium">{foodImgAttribution}</span></div>}
          <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
            <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: cat.color }}><CatIcon className="w-3 h-3" />{cat.label}</span>
            {isTopPick && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F4C430] text-gray-900">Top Pick</span>}
          </div>
          {claimed ? (
            <div className="absolute top-2.5 right-2.5 bg-emerald-500/90 backdrop-blur-md px-2.5 py-1 rounded-full flex items-center gap-1"><Award className="w-3 h-3 text-white" /><span className="text-white text-[10px] font-bold">Collected</span></div>
          ) : result.open_now ? (
            <span className="absolute top-2.5 right-2.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/90 text-white">Open Now</span>
          ) : null}
          <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1 bg-black/[0.32] backdrop-blur-sm border border-white/[0.12] px-2 py-1 rounded-full">
            <Star className="w-3 h-3 fill-[#F4C430] text-[#F4C430]" /><span className="text-white text-xs font-bold">{result.rating?.toFixed(1) || '4.5'}</span>
            <span className="text-white/50 text-xs mx-0.5">·</span><span className="text-white text-xs">{result.price || '$$'}</span>
          </div>
        </div>
        ) : (
          <div className="relative">
            <DirectionsPlaceholder
              name={result.venue_name || result.name}
              location={result.suburb || ''}
              testId={`search-${testId}`}
            />
            <div className="absolute top-2.5 left-2.5"><span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: cat.color }}><CatIcon className="w-3 h-3" />{cat.label}</span></div>
          </div>
        )}

        <div className="p-4 md:p-5">
          <h3 className="font-bold text-gray-900 text-sm leading-tight truncate">{result.name}</h3>
          {result.venue_name && result.venue_name !== result.name && <p className="text-xs text-gray-600 font-medium truncate mt-0.5">{result.venue_name}</p>}
          <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">{result.description}</p>
          <div className="flex items-center gap-3 mt-2.5 text-[11px] text-gray-400">
            {result.suburb && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{result.suburb}</span>}
            {result.distance_km != null && <span className="flex items-center gap-0.5"><Navigation className="w-3 h-3" />{result.distance_km} km</span>}
            {result.prep_time && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{result.prep_time}</span>}
            {result.difficulty && <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${result.difficulty === 'Easy' ? 'bg-green-50 text-green-600' : result.difficulty === 'Hard' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}>{result.difficulty}</span>}
          </div>
          {result.tags?.length > 0 && <div className="flex flex-wrap gap-1 mt-2.5">{result.tags.slice(0, 3).map((tag, i) => <span key={i} className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{tag}</span>)}</div>}
          {result.why_this && <p className="text-[11px] text-[#FF6B35] mt-2 italic leading-snug">"{result.why_this}"</p>}

          <EngagementBar repostCount={repostCount} onRepost={handleRepost} reposted={reposted}
            onNotMyVibe={handleNotMyVibe} vibeDisliked={vibeDisliked}
            inFoodlist={inFoodlist} foodlistCount={foodlistCount} onFoodlistToggle={handleFoodlistToggle} saveAnim={saveAnim}
            onFoodCardTap={handleFoodCardTap} claimed={claimed} claimLoading={claimLoading} testId={testId} compact />
          <ShareSection onSocialShare={handleSocialShare} shareCount={shareCount} testId={testId} compact />
        </div>
      </article>
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </>
  );
};

const MemoizedSearchVariant = memo(SearchVariant);
MemoizedSearchVariant.displayName = 'SearchVariant';
export default MemoizedSearchVariant;
